import React from "react";

import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";
import { Box, Button, Grid, TextField, Typography } from "@mui/material";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";

import { colors } from "@/theme/overrides/colors";
import { muiError, muiField, valueAsNumber } from "@/utils/hookForm";

import AddRemoveButton from "./AddRemoveButton";
import { FormValues } from "../RegisterPage";

type EntryFieldName = "education" | "trainings" | "certifications";

interface Props {
  /** Field-array name on the register form. */
  name: EntryFieldName;
  /** Section heading, e.g. "Education Details". */
  title: string;
  /** Label for the free-text field, e.g. "Institute name". */
  nameLabel: string;
  /** Label for the "add" action, e.g. "Add education". */
  addLabel: string;
}

/**
 * A repeatable list of {year, name} entries bound to a react-hook-form field
 * array. Clicking "add" appends an empty, form-bound row that the user edits
 * directly, so nothing can be silently lost by forgetting to "commit" a typed
 * value, and every input has a persistent label.
 */
const RepeatableEntryList: React.FC<Props> = ({
  name,
  title,
  nameLabel,
  addLabel,
}) => {
  const { control } = useFormContext<FormValues>();
  const { fields, append, remove } = useFieldArray<FormValues>({ name });

  return (
    <Box component="section" aria-label={title}>
      <Typography
        component="h2"
        sx={{
          fontSize: 18,
          fontWeight: 700,
          letterSpacing: "-0.5px",
          color: colors.dark,
          mb: 2,
        }}
      >
        {title}
      </Typography>
      <Grid container spacing={2} alignItems="flex-start">
        {fields.map((entry, index) => (
          <React.Fragment key={entry.id}>
            <Grid item xs={4} sm={3}>
              <Controller
                control={control}
                name={`${name}.${index}.year`}
                render={({ field, fieldState }) => (
                  <TextField
                    {...muiField(field)}
                    {...muiError(fieldState)}
                    type="number"
                    label="Year"
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={6} sm={8}>
              <Controller
                control={control}
                name={`${name}.${index}.name`}
                render={({ field, fieldState }) => (
                  <TextField
                    {...muiField(field)}
                    {...muiError(fieldState)}
                    label={nameLabel}
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={2} sm={1}>
              <AddRemoveButton
                variant="contained"
                disableElevation
                aria-label={`Remove ${title} entry ${index + 1}`}
                onClick={() => remove(index)}
              >
                <RemoveIcon />
              </AddRemoveButton>
            </Grid>
          </React.Fragment>
        ))}
        <Grid item xs={12}>
          <Button
            variant="text"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => append({ name: "", year: valueAsNumber("") })}
          >
            {addLabel}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RepeatableEntryList;
